'use client';

import Image from 'next/image';
import type { GroupedProductItem } from '@cobrastyle/shared-types';
import { Text } from '@cobrastyle/ui';
// Not the design system's <Price>, which always prints two decimals — see
// lib/format-price. A grouped child and the same product on its own page must
// not show different figures.
import { formatPrice } from '@/lib/format-price';
import { QuantitySelector } from './quantity-selector';

interface GroupedProductItemsProps {
  items: GroupedProductItem[];
  quantities: Record<string, number>;
  onChange: (sku: string, quantity: number) => void;
}

export function GroupedProductItems({ items, quantities, onChange }: GroupedProductItemsProps) {
  return (
    <div className="space-y-3">
      {items.map((item) => (
        <div
          key={item.product.sku}
          className="flex items-center justify-between gap-4 rounded-xl border border-border bg-card-hover p-4"
        >
          <div className="flex items-center gap-4 flex-1 min-w-0">
            {item.product.images[0] && (
              <div className="relative h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg border border-border">
                <Image
                  src={item.product.images[0].url}
                  alt={item.product.name}
                  fill
                  className="object-cover"
                  sizes="64px"
                />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <Text size="sm" weight="medium" className="text-foreground truncate">
                {item.product.name}
              </Text>
              <Text size="sm" className="text-price font-semibold">
                {formatPrice(item.product.price.final, item.product.price.currency)}
              </Text>
            </div>
          </div>
          <QuantitySelector
            value={quantities[item.product.sku] ?? 0}
            onChange={(value) => onChange(item.product.sku, value)}
            min={0}
          />
        </div>
      ))}
    </div>
  );
}
